import { useState, type FormEvent } from 'react';
import styled, { keyframes } from 'styled-components';
import { Modal } from './Modal';
import { Button } from './Button';
import { Field, Input, Label } from './Input';

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(2px); }
`;

const DangerBox = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.danger}15;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  line-height: 1.55;
`;

const Form = styled.form<{ $invalid: boolean }>`
  animation: ${({ $invalid }) => ($invalid ? shake : 'none')} 360ms ease;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const DangerButton = styled(Button)`
  background: ${({ theme }) => theme.colors.status.danger};
  border-color: ${({ theme }) => theme.colors.status.danger};
  color: #fff;
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.status.danger};
    filter: brightness(1.1);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface Props {
  open: boolean;
  title: string;
  warning: string;
  /// Текст, который пользователь должен ввести для подтверждения опасной
  /// операции. Регистр игнорируется, пробелы по краям обрезаются.
  confirmText: string;
  /// Лейбл для поля ввода: «Введите название контрагента»
  confirmLabel: string;
  confirmButtonLabel?: string;
  /// Текст пока идёт операция (replaces confirm button label)
  busyLabel?: string;
  isBusy?: boolean;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  warning,
  confirmText,
  confirmLabel,
  confirmButtonLabel = 'Удалить',
  busyLabel = 'Удаление…',
  isBusy = false,
  onConfirm,
  onCancel,
}: Props) {
  const [value, setValue] = useState('');
  const [invalid, setInvalid] = useState(false);

  const matches = value.trim().toLowerCase() === confirmText.trim().toLowerCase();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!matches) {
      setInvalid(true);
      setTimeout(() => setInvalid(false), 400);
      return;
    }
    await onConfirm();
    setValue('');
  }

  function handleClose() {
    if (isBusy) return;
    setValue('');
    setInvalid(false);
    onCancel();
  }

  return (
    <Modal open={open} title={title} onClose={handleClose}>
      <DangerBox>{warning}</DangerBox>
      <Form onSubmit={onSubmit} $invalid={invalid}>
        <Field>
          <Label htmlFor="confirm-text">{confirmLabel}</Label>
          <Input
            id="confirm-text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={confirmText}
            autoFocus
            disabled={isBusy}
          />
        </Field>
        <Actions>
          <Button type="button" onClick={handleClose} disabled={isBusy}>
            Отмена
          </Button>
          <DangerButton type="submit" disabled={!matches || isBusy}>
            {isBusy ? busyLabel : confirmButtonLabel}
          </DangerButton>
        </Actions>
      </Form>
    </Modal>
  );
}
